import React, { useState, useEffect } from 'react';
import { Spinner, Form, Row, Col } from 'react-bootstrap';
// import { useAuth, api } from '../context/AuthContext';
import api from '../api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import './AnalyticsDashboard.css'

const AnalyticsDashboard = () => {
  const [topProducts, setTopProducts] = useState([]);
  const [dayPerformance, setDayPerformance] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num).replace('KES', 'Ksh');
  };

  const formatPercentage = (value) => {
    const num = Number(value) || 0;
    return `${num.toFixed(2)}%`;
  };

  //handlers
  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  
  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      const responses = await Promise.all([
        api.get('/salesanalytics/top-products', { params }),
        api.get('/salesanalytics/day-performance', { params }),
        api.get('/salesanalytics/payment-methods', { params })
      ]);
      setTopProducts(responses[0].data.filter(p => p.productId && p.totalQuantity > 0));
      setDayPerformance(responses[1].data);
      setPaymentMethods(responses[2].data);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) return <div className="text-center p-4"><Spinner /> Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  const renderTableRow = (items) => items.map(([label, value]) => (
    <tr key={label}>
      <td>{label}</td>
      <td colSpan="3" className="text-center text-muted">{value}</td>
    </tr>
  ));

  return (
    <div className="container mt-4">
      {/* Title and date selectors */}
      <h2 className="mb-4">Sales Analytics Dashboard</h2>

      <Form className="mb-4">
        <Row>
          <Col md={6}>
            <Form.Group controlId="selectMonth">
              <Form.Label>Month</Form.Label>
              <Form.Select value={month} onChange={e => setMonth(Number(e.target.value))}>
                {months.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="selectYear">
              <Form.Label>Year</Form.Label>
              <Form.Select value={year} onChange={e => setYear(Number(e.target.value))}>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted">Total Revenue</h6>
              <h4 className="fw-bold text-success">
                {formatCurrency(
                  dayPerformance.reduce((sum, d) => sum + (d.totalSales || 0), 0)
                )}
              </h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted">Avg Daily Revenue</h6>
              <h4 className="fw-bold text-primary">
                {formatCurrency(
                  dayPerformance.length
                    ? dayPerformance.reduce((sum, d) => sum + (d.totalSales || 0), 0) / dayPerformance.length
                    : 0
                )}
              </h4>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body text-center">
              <h6 className="text-muted">Top Product</h6>
              <h5 className="fw-bold text-dark">
                {topProducts.length ? topProducts[0].productName || 'Unknown' : 'N/A'}
              </h5>
              <small className="text-muted">
                Sold: {topProducts.length ? Number(topProducts[0].totalQuantity).toLocaleString() : 0}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Trends */}
      <div className="row mb-4">
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Average Sale per Day (Line Chart)</h5>
            </div>
            <div className="card-body" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dayPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dayName" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="averageSale" stroke="#007bff" name="Avg Sale (KES)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Daily Sales (Bar Chart)</h5>
            </div>
            <div className="card-body" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dayPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dayName" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="totalSales" fill="#28a745" name="Total Sales (KES)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Tables */}
      <div className="row">
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light">
              <h5>Daily Performance</h5>
            </div>
            <div className="card-body p-0">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Total Sales</th>
                    <th>Avg Sale</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {dayPerformance.map((day) => (
                    <tr key={day.dayName}>
                      <td>{day.dayName}</td>
                      <td>{formatCurrency(day.totalSales)}</td>
                      <td>{day.averageSale > 0 ? formatCurrency(day.averageSale) : 'N/A'}</td>
                      <td>{day.transactionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Top Selling Products</h5>
            </div>
            <div className="card-body p-0">
              <table className="table mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Product</th>
                    <th className="text-end">Quantity</th>
                    <th className="text-end">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(topProducts) && topProducts.length > 0 ? (
                    topProducts.map((product, index) => {
                      const isIdentified = product.isIdentified;
                      const productId = product.productId || `missing-id-${index}`;
                      const productName = product.productName || 'Unknown Product';
                      return (
                        <tr key={productId}>
                          <td className="align-middle">
                            {isIdentified ? (
                              productName
                            ) : (
                              <div>
                                <span className="text-danger">
                                  Unidentified Product (ID: {productId})
                                </span>
                                <small className="text-muted d-block">
                                  Product not found in database
                                </small>
                              </div>
                            )}
                          </td>
                          <td className="text-end align-middle">
                            {Number(product.totalQuantity).toLocaleString()}
                          </td>
                          <td className="text-end align-middle">
                            {formatCurrency(Number(product.totalRevenue))}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-3">
                        <span className="text-muted">No valid product sales found</span>
                        <br />
                        <small className="text-muted">
                          (Products need both quantity and price data to appear)
                        </small>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Section */}
      <div className="card">
        <div className="card-header bg-light">
          <h5 className="mb-0">Payment Method Breakdown</h5>
        </div>
        <div className="card-body p-0">
          <table className="table mb-0">
            <thead className="bg-light">
              <tr>
                <th>Method</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Count</th>
                <th className="text-end">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {paymentMethods.length ? 
                paymentMethods.map((method) => (
                  <tr key={method.paymentType}>
                    <td>{method.paymentType}</td>
                    <td className="text-end">{formatCurrency(method.totalAmount)}</td>
                    <td className="text-end">{method.transactionCount || 0}</td>
                    <td className="text-end">{formatPercentage(method.percentage)}</td>
                  </tr>
                )) : 
                renderTableRow([["No payment method data", ""]])
              }
            </tbody>
          </table>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Payment Methods Distribution</h5>
            </div>
            <div className="card-body d-flex justify-content-center align-items-center" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentMethods}
                    dataKey="totalPaid"
                    nameKey="methodName"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    fill="#8884d8"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {paymentMethods.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#8884d8' : '#82ca9d'} />
                    ))}
                  </Pie>
                  <Legend verticalAlign="bottom" height={36} />
                  <Tooltip formatter={value => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
